// Spreading selling points across the takes of one structured run.
//
// With no variation axis, the takes would otherwise differ only by the model's
// own randomness. Giving each take a different SUBSET of the chosen points makes
// them differ by what they actually claim — which is the difference that matters
// when picking a winner.
//
// A subset, not a single point: a product can have a dozen true things to say,
// and one claim per ad throws away the ones that support it.

/** Fisher-Yates on a copy. */
function shuffled<T>(items: T[]): T[] {
  const out = [...items];
  for (let i = out.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

/** All k-element index combinations of [0, n), in lexicographic order. */
function combinations(n: number, k: number): number[][] {
  const out: number[][] = [];
  const combo = Array.from({ length: k }, (_, i) => i);
  for (;;) {
    out.push([...combo]);
    // Advance the rightmost index that still has room to move.
    let i = k - 1;
    while (i >= 0 && combo[i] === n - k + i) i -= 1;
    if (i < 0) return out;
    combo[i] += 1;
    for (let j = i + 1; j < k; j += 1) combo[j] = combo[j - 1] + 1;
  }
}

/**
 * C(n, k) with the same clamping rotateSellingPoints applies, so the two agree
 * on when repeats begin.
 */
export function distinctMixCount(pointCount: number, perTake: number): number {
  const n = Math.floor(pointCount) || 0;
  if (n <= 0) return 1;
  const k = Math.max(1, Math.min(Math.floor(perTake) || 1, n));
  let c = 1;
  for (let i = 0; i < k; i += 1) c = (c * (n - i)) / (i + 1);
  return Math.round(c);
}

/**
 * One subset per take.
 *
 * Shuffled once, then each take greedily claims the unused combination whose
 * points have been featured least (ties broken in lexicographic pool order):
 * distinct mixes whenever enough combinations exist, with coverage spread
 * across the whole pool. When takes outnumber the distinct combinations, the
 * mixes repeat in take order (take N+1 matches take 1) — the caller is
 * expected to say so rather than promise variety that cannot exist. The pool
 * is operator-ticked points (a dozen at most), so enumerating C(n, k)
 * combinations is cheap.
 *
 * @param points   the operator's chosen points
 * @param takes    how many creatives are being generated
 * @param perTake  how many points each one should feature
 * @returns one array per take; an empty input yields empty subsets
 */
export function rotateSellingPoints(points: string[], takes: number, perTake: number): string[][] {
  const clean = (points || []).filter(p => typeof p === 'string' && p.trim());
  const runs = Math.max(1, Math.floor(takes) || 1);
  if (!clean.length) return Array.from({ length: runs }, () => []);

  const pool = shuffled(clean);
  const size = Math.max(1, Math.min(Math.floor(perTake) || 1, pool.length));

  const combos = combinations(pool.length, size);
  const usage = new Array(pool.length).fill(0);
  const claimed = combos.map(() => false);
  const picked: number[][] = [];
  for (let take = 0; take < Math.min(runs, combos.length); take += 1) {
    let best = -1;
    let bestScore = Infinity;
    combos.forEach((combo, i) => {
      if (claimed[i]) return;
      const score = combo.reduce((sum, p) => sum + usage[p], 0);
      if (score < bestScore) { bestScore = score; best = i; }
    });
    claimed[best] = true;
    for (const p of combos[best]) usage[p] += 1;
    picked.push(combos[best]);
  }

  return Array.from(
    { length: runs },
    (_, take) => picked[take % picked.length].map(i => pool[i]),
  );
}

/**
 * The most points per take that makes sense for a selection.
 *
 * Half the pool, capped: past a handful the ad is a list rather than a claim,
 * and the format's slots run out.
 */
export function defaultPerTake(pointCount: number): number {
  if (pointCount <= 1) return 1;
  return Math.max(1, Math.min(3, Math.ceil(pointCount / 2)));
}
